/** Serverzustand der Stammdatenverwaltung. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createCategory,
  createMasterData,
  deleteCategory,
  deleteMasterData,
  getPriorityMatrix,
  listCategories,
  listMasterData,
  replacePriorityMatrix,
  updateCategory,
  updateMasterData,
  type MasterDataKind,
  type MatrixCell,
} from './api';

export const masterDataKeys = {
  all: ['masterdata'] as const,
  list: (kind: MasterDataKind) => ['masterdata', kind] as const,
  categories: ['masterdata', 'categories'] as const,
  matrix: ['masterdata', 'matrix'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

/**
 * Stammdaten aendern sich selten.
 *
 * Fuenf Minuten Frische statt der ueblichen dreissig Sekunden: Auswahllisten
 * werden auf jeder Seite gebraucht, und ein Nachladen bei jedem Wechsel waere
 * Last ohne Gewinn. Nach einer Aenderung wird ohnehin gezielt entwertet.
 */
const MASTER_DATA_STALE_TIME = 5 * 60 * 1000;

export function useMasterData(kind: MasterDataKind) {
  return useQuery({
    queryKey: masterDataKeys.list(kind),
    queryFn: () => listMasterData(kind),
    staleTime: MASTER_DATA_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useCreateMasterData(kind: MasterDataKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createMasterData(kind, payload),
    onSuccess: async () => {
      // Die gesamte Stammdatengruppe entwerten und nicht nur die eine Liste:
      // Ein neuer Vorgabeeintrag nimmt dem bisherigen sein Kennzeichen, und
      // die Prioritaetsmatrix haengt an den Achsen.
      await queryClient.invalidateQueries({ queryKey: masterDataKeys.all });
    },
  });
}

export function useUpdateMasterData(kind: MasterDataKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      updateMasterData(kind, id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataKeys.all });
    },
  });
}

export function useDeleteMasterData(kind: MasterDataKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMasterData(kind, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataKeys.all });
    },
  });
}

// --- Kategorien -------------------------------------------------------------

export function useCategories() {
  return useQuery({
    queryKey: masterDataKeys.categories,
    queryFn: listCategories,
    staleTime: MASTER_DATA_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; parent_id?: string | null }) => createCategory(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataKeys.categories });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      updateCategory(id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataKeys.categories });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataKeys.categories });
    },
  });
}

// --- Prioritaetsmatrix ------------------------------------------------------

export function usePriorityMatrix() {
  return useQuery({
    queryKey: masterDataKeys.matrix,
    queryFn: getPriorityMatrix,
    staleTime: MASTER_DATA_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useReplacePriorityMatrix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cells: MatrixCell[]) => replacePriorityMatrix(cells),
    onSuccess: (matrix) => {
      queryClient.setQueryData(masterDataKeys.matrix, matrix);
    },
  });
}
